import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Form,
    FormField,
    Dropdown,
    TextArea,
    Button,
    Message,
    Header,
    Radio,
    Segment,
} from 'semantic-ui-react';
import { LoadingWrapper } from '../../common';
import { IcctDatePicker } from '../../common/IcctDatePicker';
import { fetchWithAuth, getCSRFToken, useAuth } from '../../auth';
import { createRoadblock, getRoadblockTypes, FieldValidationError } from './roadblockService';
import { listDFNs } from '../../DFN/dfnService';
import { formatDateToYyyyMmDd } from '../../../utils/formatDate';
import { getAllMembers } from '../../common/apiClient';
import { getEligibleMemberOptions } from './constants';

const MAX_DESCRIPTION = 500;
const MAX_OTHER_DESCRIPTION = 255;

const defaultForm = {
    team: '',
    date: null,
    roadblock_type: null,
    other_description: '',
    roadblock_subtype: null,
    dfn: null,
    assigned_to: null,
    production_impact: false,
    description: '',
};

export const RoadblockForm = () => {
    const navigate = useNavigate();
    const { isReadOnly } = useAuth();
    const [form, setForm] = useState(defaultForm);
    const [teams, setTeams] = useState([]);
    const [typeOptions, setTypeOptions] = useState([]);
    const [dfnOptions, setDfnOptions] = useState([]);
    const [allMembers, setAllMembers] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [inputErrors, setInputErrors] = useState({});

    useEffect(() => {
        const loadData = async () => {
            try {
                const [teamsResp, types, dfnsResp, members] = await Promise.all([
                    fetchWithAuth(`${process.env.REACT_APP_ICC_TOOL_API_HOST}roadblocks/teams/`, {
                        headers: { 'X-CSRFToken': getCSRFToken() },
                        credentials: 'include',
                    }),
                    getRoadblockTypes(),
                    listDFNs(),
                    getAllMembers(),
                ]);
                const teamsData = await teamsResp.json();
                setTeams(teamsData.map(t => ({ key: t.id, value: t.id, text: t.name })));
                setTypeOptions(types.map(t => ({ key: t.id, value: t.id, text: t.label, requiresDescription: t.requiresDescription, subtypes: t.subtypes || [] })));
                const dfns = await dfnsResp.json();
                setDfnOptions(dfns.map(d => ({ key: d.id, value: d.id, text: d.name })));
                setAllMembers(members);
            } catch (err) {
                setError('Failed to load form options.');
            } finally {
                setLoaded(true);
            }
        };
        loadData();
    }, []);

    const eligibleMemberOptions = useMemo(
        () => getEligibleMemberOptions(allMembers, form.team),
        [allMembers, form.team]
    );

    const handleChange = (key, value) => {
        setForm(prev => {
            const next = { ...prev, [key]: value };
            if (key === 'team' && prev.assigned_to) {
                const eligible = getEligibleMemberOptions(allMembers, value);
                if (!eligible.some(m => m.value === prev.assigned_to)) next.assigned_to = null;
            }
            if (key === 'roadblock_type') next.roadblock_subtype = null;
            return next;
        });
        setInputErrors(prev => ({
            ...prev,
            [key]: null,
            ...(key === 'team' && { assigned_to: null }),
            ...(key === 'roadblock_type' && { roadblock_subtype: null }),
        }));
    };

    const selectedTypeObj = typeOptions.find(t => t.value === form.roadblock_type);
    const isOther = selectedTypeObj?.requiresDescription ?? false;
    const subtypeOptions = (selectedTypeObj?.subtypes || []).map(st => ({ key: st.id, value: st.id, text: st.label }));

    const validate = () => {
        const errors = {};
        if (!form.team) errors.team = 'Team is required.';
        if (!form.date) errors.date = 'Date is required.';
        if (!form.roadblock_type) errors.roadblock_type = 'Roadblock type is required.';
        if (isOther && !form.other_description.trim())
            errors.other_description = 'Please describe the "Other" roadblock type.';
        if (!form.dfn) errors.dfn = 'DFN is required.';
        if (!form.description.trim()) errors.description = 'Description is required.';
        setInputErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSaving(true);
        setError('');
        try {
            await createRoadblock({
                team: form.team,
                date: formatDateToYyyyMmDd(form.date),
                roadblock_type: form.roadblock_type,
                other_description: isOther ? form.other_description.trim() : undefined,
                roadblock_subtype: form.roadblock_subtype || null,
                dfn: form.dfn,
                assigned_to: form.assigned_to || undefined,
                production_impact: form.production_impact,
                description: form.description.trim(),
            });
            navigate('/forms/roadblock');
        } catch (err) {
            if (err instanceof FieldValidationError) {
                setInputErrors(err.fields);
            } else {
                setError(err.message);
            }
        } finally {
            setSaving(false);
        }
    };

    const handleClear = () => {
        setForm(defaultForm);
        setInputErrors({});
        setError('');
    };

    const isSubmitDisabled =
        !form.team || !form.date || !form.roadblock_type || !form.dfn || !form.description.trim() ||
        (isOther && !form.other_description.trim());

    return (
        <LoadingWrapper loading={!loaded}>
            <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem' }}>
                <div style={{ width: '100%', maxWidth: '600px' }}>
                    <Header as="h2">New Roadblock Form</Header>

                    {error && <Message negative>{error}</Message>}

                    <Segment>
                        <Form>
                            <FormField required error={!!inputErrors.team}>
                                <label>Team Name</label>
                                <Dropdown
                                    search
                                    selection
                                    placeholder="Select a team..."
                                    options={teams}
                                    value={form.team}
                                    onChange={(e, { value }) => handleChange('team', value)}
                                />
                                {inputErrors.team && (
                                    <div style={{ color: '#9f3a38', fontSize: '0.85em', marginTop: '0.25em' }}>
                                        {inputErrors.team}
                                    </div>
                                )}
                            </FormField>

                            <FormField required error={!!inputErrors.date}>
                                <label>Date of Roadblock Start</label>
                                <IcctDatePicker
                                    keyName="date"
                                    value={form.date}
                                    onChange={handleChange}
                                    error={!!inputErrors.date}
                                />
                                {inputErrors.date && (
                                    <div style={{ color: '#9f3a38', fontSize: '0.85em', marginTop: '0.25em' }}>
                                        {inputErrors.date}
                                    </div>
                                )}
                            </FormField>

                            <FormField required error={!!inputErrors.roadblock_type}>
                                <label>Type of Roadblock</label>
                                <Dropdown
                                    search
                                    selection
                                    placeholder="Select a roadblock type..."
                                    options={typeOptions}
                                    value={form.roadblock_type}
                                    onChange={(e, { value }) => handleChange('roadblock_type', value)}
                                />
                                {inputErrors.roadblock_type && (
                                    <div style={{ color: '#9f3a38', fontSize: '0.85em', marginTop: '0.25em' }}>
                                        {inputErrors.roadblock_type}
                                    </div>
                                )}
                            </FormField>

                            {isOther && (
                                <FormField required error={!!inputErrors.other_description}>
                                    <label>Other — Please Specify</label>
                                    <TextArea
                                        placeholder="Briefly describe the roadblock type..."
                                        maxLength={MAX_OTHER_DESCRIPTION}
                                        value={form.other_description}
                                        onChange={(e, { value }) => handleChange('other_description', value)}
                                        rows={2}
                                    />
                                    <div style={{ color: '#666', fontSize: '0.85em', marginTop: '0.25em', textAlign: 'right' }}>
                                        {form.other_description.length}/{MAX_OTHER_DESCRIPTION} characters
                                    </div>
                                    {inputErrors.other_description && (
                                        <div style={{ color: '#9f3a38', fontSize: '0.85em' }}>
                                            {inputErrors.other_description}
                                        </div>
                                    )}
                                </FormField>
                            )}

                            {subtypeOptions.length > 0 && (
                                <FormField error={!!inputErrors.roadblock_subtype}>
                                    <label>Subtype <span style={{ color: '#999', fontWeight: 'normal' }}>(optional)</span></label>
                                    <Dropdown
                                        search
                                        selection
                                        clearable
                                        placeholder="Select a subtype..."
                                        options={subtypeOptions}
                                        value={form.roadblock_subtype ?? ''}
                                        onChange={(e, { value }) => handleChange('roadblock_subtype', value || null)}
                                    />
                                    {inputErrors.roadblock_subtype && (
                                        <div style={{ color: '#9f3a38', fontSize: '0.85em', marginTop: '0.25em' }}>
                                            {inputErrors.roadblock_subtype}
                                        </div>
                                    )}
                                </FormField>
                            )}

                            <FormField required error={!!inputErrors.dfn}>
                                <label>DFN</label>
                                <Dropdown
                                    search
                                    selection
                                    placeholder="Select a DFN..."
                                    options={dfnOptions}
                                    value={form.dfn}
                                    onChange={(e, { value }) => handleChange('dfn', value)}
                                />
                                {inputErrors.dfn && (
                                    <div style={{ color: '#9f3a38', fontSize: '0.85em', marginTop: '0.25em' }}>
                                        {inputErrors.dfn}
                                    </div>
                                )}
                            </FormField>

                            <FormField error={!!inputErrors.assigned_to}>
                                <label>Assigned To <span style={{ color: '#999', fontWeight: 'normal' }}>(optional)</span></label>
                                <Dropdown
                                    search
                                    selection
                                    clearable
                                    placeholder="Select a member..."
                                    options={eligibleMemberOptions}
                                    value={form.assigned_to ?? ''}
                                    onChange={(e, { value }) => handleChange('assigned_to', value || null)}
                                />
                                {inputErrors.assigned_to && (
                                    <div style={{ color: '#9f3a38', fontSize: '0.85em', marginTop: '0.25em' }}>
                                        {inputErrors.assigned_to}
                                    </div>
                                )}
                            </FormField>

                            <FormField>
                                <label>Production Impact</label>
                                <Form.Group inline>
                                    <Radio
                                        label="Yes"
                                        name="production_impact"
                                        checked={form.production_impact === true}
                                        onChange={() => handleChange('production_impact', true)}
                                    />
                                    <Radio
                                        label="No"
                                        name="production_impact"
                                        style={{ marginLeft: '1rem' }}
                                        checked={form.production_impact === false}
                                        onChange={() => handleChange('production_impact', false)}
                                    />
                                </Form.Group>
                            </FormField>

                            <FormField>

                                <label>Roadblock Description</label>
                                <TextArea
                                    placeholder="Describe the roadblock..."
                                    maxLength={MAX_DESCRIPTION}
                                    value={form.description}
                                    onChange={(e, { value }) => handleChange('description', value)}
                                    rows={4}
                                />
                                <div style={{ color: '#666', fontSize: '0.85em', marginTop: '0.25em', textAlign: 'right' }}>
                                    {form.description.length}/{MAX_DESCRIPTION} characters
                                </div>
                                {inputErrors.description && (
                                    <div style={{ color: '#9f3a38', fontSize: '0.85em' }}>
                                        {inputErrors.description}
                                    </div>
                                )}
                            </FormField>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1rem' }}>
                                <Button type="button" onClick={handleClear} disabled={saving}>
                                    Clear All
                                </Button>
                                {!isReadOnly && (
                                    <Button
                                        primary
                                        type="button"
                                        onClick={handleSubmit}
                                        loading={saving}
                                        disabled={isSubmitDisabled || saving}
                                    >
                                        Submit
                                    </Button>
                                )}
                            </div>
                        </Form>
                    </Segment>
                </div>
            </div>
        </LoadingWrapper>
    );
};
